/// <reference types="kendo-ui" />

/**
 * Concrete implementation of Hicom site search
 */
class HicomSiteSearch {
    private siteSearch: HTMLElement;
    private searchClear: HTMLElement | null;
    private searchResults: HTMLElement | null;
    private searchNoResults: HTMLElement | null;
    private searchKeywords: HTMLInputElement | null;
    private viewModel = kendo.observable({
        results: []
    });
    private _page: HicomPage;

    /**
     * Initializes a new instance of the HicomSiteSearch class.
     * @param siteSearchId The ID of the site search element.
     */
    constructor(siteSearchId: string, hicomPage: HicomPage) {
        this._page = hicomPage;
        this.siteSearch = document.getElementById(siteSearchId) as HTMLElement;
        this.searchClear = this.siteSearch.getElementsByClassName('siteSearch-clear')[0] as HTMLElement | null;
        this.searchResults = this.siteSearch.getElementsByClassName('siteSearch-results')[0] as HTMLElement | null;
        this.searchNoResults = this.siteSearch.getElementsByClassName('siteSearch-noresults')[0] as HTMLElement | null;
        this.searchKeywords = this.siteSearch.getElementsByClassName('siteSearch-keywords')[0] as HTMLInputElement | null;

        this.addEventListeners();

        // Move modal
        document.body.appendChild(this.siteSearch);

        // Setup kendo binding
        if (this.searchResults != null) {
            kendo.bind(this.searchResults, this.viewModel);
        }
    }

    private debounce(func, timeout = 300) {
        let timer;
        return (...args) => {
            clearTimeout(timer);
            timer = setTimeout(() => { func.apply(this, args); }, timeout);
        };
    }

    private formatPrettyUrl(url): string {
        const parts = url.split('//') as string[];
        return parts[0] + '//' + parts[1].replace(/\//g, ' › ');
    }

    private search(keywords): void {
        const encodedKeywords = encodeURIComponent(keywords)
        this._page.fetchGet(`${this._page.BaseUrl}/WebApi/Search?keywords=${encodedKeywords}`)
            .catch((error) => {
                console.error('SiteSearch::Error:', error);
            })
            .then((rawData: string | null | undefined) => {
                if (rawData) {
                    const data = JSON.parse(rawData);
                    const results = (data.Results || []).map(item => ({
                        ...item,
                        PrettyUrl: this.formatPrettyUrl(item.Url)
                    }));

                    this.viewModel.set("results", results);

                    this.afterResults(results.length > 0);
                }
            });
    }

    private afterResults(hasResults) {
        this.toggle(this.searchNoResults, !hasResults)
        this.toggle(this.searchResults, hasResults)
    }

    private toggle(element: HTMLElement, visible: boolean) {
        element.style.display = visible ? 'block' : 'none';
    }

    /**
     * Adds event listeners to the site search element.
     */
    private addEventListeners(): void {

        this.siteSearch.addEventListener('shown.bs.modal', () => {
            this.searchKeywords.focus();
        })

        const debouncedSearch = this.debounce(this.search, 300);

        this.searchKeywords.addEventListener('keyup', () => {
            const keywords = this.searchKeywords.value.trim();
            if (keywords.length < 3) {
                this.viewModel.set("results", []);
                this.toggle(this.searchClear, false);
                this.afterResults(false);
            } else {
                debouncedSearch(keywords)
                this.toggle(this.searchClear, true);
            }
        });

        this.searchClear.addEventListener('click', () => {
            this.viewModel.set("results", []);
            this.searchKeywords.value = '';
            this.toggle(this.searchClear, false);
            this.afterResults(false)
        });
    }
}